// Subheadings inside a client scope item.
//
// An inclusion is a plain string (a work step, NOTE, images, standard or
// cleanup line) or a heading object `{ type: 'heading', text }`. Stage 4 and
// Stage 5 group the lines under each heading object and print it as a section
// heading; a string prints as a bullet even when it is written in capitals.
//
// Stage 3 writes a subheading as an ALL-CAPS step in sequence_of_works, and
// every Stage 3 -> 4 producer copies a step's text as a plain string. This
// module turns the lines Stage 3 meant as subheadings into heading objects.
// It never inserts, removes, reorders or rewords a line: a converted line keeps
// its exact words and its position. Producers convert as soon as an item's
// lines exist, before any pass that reads every string as a work line. The
// passes that remove lines (bookends, logistics de-dupe, repeat removal) go
// through filterInclusionLines, so a heading is never taken for a protection
// line and never left with nothing under it. An exact repeat of a work line is
// removed under a subheading of the same text or of the same area
// (dedupeInclusionLines). Under any other subheading the same words are kept:
// another unit's, or an area whose name cannot be read ("ENSUITE"), is that
// area's work. A NOTE, images, protection or cleanup line belongs to the whole
// item and is listed once.
//
// Two ways to know a line is a subheading:
//   1. Lineage: the line is the text of a source step marked
//      `kind: 'subheading'`. A source step with any other `kind` keeps its line
//      a work line, whatever its letter case.
//   2. A strict text test, used only for lines with no kind on their step and
//      only when the item has at least SUBHEADING_MIN_TEXT_LINES subheadings
//      (such lines plus the subheadings it already has), so one shouted work
//      step is never turned into a heading.
//
// Builder-edited rows are never converted automatically. The screens offer a
// one-click "Make these subheadings" instead, using
// findSubheadingLineIndexes + convertSubheadingLines.
//
// House structure for every business. The numbers live here, in one place, so
// a later per-business rulebook can override them.

import { stage3SequenceStepLabel } from '../utils/stage3-work-method';
// scope-copy-hygiene imports filterInclusionLines from this module. The cycle
// is safe: neither module calls the other while it loads.
import { isPhysicalScopeBookendLine } from '../utils/scope-copy-hygiene';
import { extractUnitLocatorsFromText } from '../utils/scope-unit-locators';
import { hasOperatorInclusionPricingControl } from '../utils/inclusions-helpers';

/** The inclusion `type` Stage 4 and Stage 5 render as a section heading. */
export const SUBHEADING_INCLUSION_TYPE = 'heading' as const;
/** The Stage 3 sequence step `kind` that marks a subheading line. */
export const SUBHEADING_STEP_KIND = 'subheading' as const;
/** A text-test subheading has at most this many words. */
export const SUBHEADING_MAX_WORDS = 10;
/** The text test converts only when an item has at least this many matching lines. */
export const SUBHEADING_MIN_TEXT_LINES = 2;

export interface HeadingInclusion {
    type: typeof SUBHEADING_INCLUSION_TYPE;
    text: string;
    [key: string]: unknown;
}

export interface SubheadingSourceOptions {
    /** Stage 3 sequence steps the item's lines were copied from. */
    sourceSteps?: unknown[];
}

type UnknownRecord = Record<string, unknown>;

function record(value: unknown): UnknownRecord | null {
    return value && typeof value === 'object' && !Array.isArray(value) ? value as UnknownRecord : null;
}

function clean(value: unknown): string {
    return typeof value === 'string' ? value.replace(/\s+/g, ' ').trim() : '';
}

export function isHeadingInclusion(value: unknown): value is HeadingInclusion {
    const row = record(value);
    return Boolean(row && row.type === SUBHEADING_INCLUSION_TYPE);
}

// Imperative verbs a work step opens with. Mirrors the scorer's list in
// src/utils/scope-structure-check.ts (STEP_VERBS), which no code that builds
// a quote may call. A "re" or "re-" prefix on a listed verb also counts.
const STEP_VERBS = new Set([
    'abrade', 'access', 'adhere', 'adjust', 'align', 'anchor', 'apply', 'backfill', 'batch', 'bed', 'bend',
    'blast', 'blend', 'block', 'bolt', 'bond', 'bore', 'brace', 'break', 'brush', 'buff', 'build', 'cap',
    'carry', 'cart', 'caulk', 'chase', 'check', 'chip', 'clad', 'clean', 'clear', 'close', 'coat', 'commission',
    'compact', 'confirm', 'connect', 'construct', 'convert', 'core', 'cover', 'cure', 'cut', 'deliver',
    'demolish', 'detail', 'disconnect', 'dismantle', 'dispose', 'divert', 'document', 'dowel', 'drain',
    'dress', 'drill', 'dry', 'dye', 'engage', 'erect', 'excavate', 'extend', 'fabricate', 'fasten', 'feather',
    'fill', 'finish', 'fit', 'fix', 'flash', 'float', 'flood', 'flush', 'fold', 'form', 'frame', 'glue',
    'grade', 'grind', 'grout', 'hang', 'hire', 'hone', 'inject', 'inspect', 'install', 'insulate', 'isolate',
    'issue', 'join', 'joint', 'lap', 'lay', 'level', 'lift', 'line', 'load', 'locate', 'make', 'mark', 'mask',
    'match', 'measure', 'mitre', 'mix', 'mobilise', 'monitor', 'mount', 'nail', 'neutralise', 'notch', 'oil',
    'open', 'pack', 'paint', 'patch', 'pin', 'place', 'plaster', 'plumb', 'point', 'polish', 'position',
    'pour', 'prepare', 'pressure', 'prime', 'profile', 'prop', 'protect', 'provide', 'record', 'relocate',
    'remove', 'render', 'repair', 'replace', 'reinforce', 'renew', 'restrain', 'rinse', 'roll', 'route',
    'rout', 'sand', 'saturate', 'saw', 'scabble', 'scrape', 'screed', 'screw', 'scrub', 'seal', 'secure',
    'set', 'shape', 'sheet', 'shore', 'skim', 'slope', 'soak', 'spray', 'stain', 'staple', 'stitch', 'strip',
    'supply', 'support', 'sweep', 'tamp', 'terminate', 'test', 'tie', 'tile', 'tool', 'touch', 'transport',
    'treat', 'trench', 'trim', 'trowel', 'turn', 'undercoat', 'underpin', 'undertake', 'vacuum', 'varnish',
    'wash', 'waterproof', 'weld', 'wet', 'wire', 'jet', 'rake', 'reinstate', 'reinstall', 'refit', 'reseal',
    'redetail', 'repoint', 'retile', 'relay', 'rebuild', 'recoat', 'refix', 'resecure', 'regrade', 'reroute',
    'photograph', 'dig', 'slab', 'grit', 'pre-treat', 'pretreat', 'hydro', 'sandblast', 'pump', 'vent',
    'allow', 'jackhammer', 'hack', 'chisel', 'haul', 'hydroblast',
]);

const LEADING_ADVERBS = new Set([
    'mechanically', 'thoroughly', 'carefully', 'neatly', 'fully', 'partially', 'temporarily', 'manually',
    'lightly', 'progressively', 'selectively',
]);

// A building-code reference never belongs in a subheading.
const STANDARD_NUMBER_RE = /\b(?:AS(?:\s*\/\s*NZS)?|NZS|NCC|BCA)\s*\d/i;
const NOTE_LINE_RE = /^NOTE\b/i;
const IMAGES_LINE_RE = /^refer to (?:the )?(?:attached |provided )?(?:images|photos|photographs)\b/i;

function opensWithStepVerb(text: string): boolean {
    const words = text
        .toLowerCase()
        .split(/[\s,]+/)
        .map(word => word.replace(/^[^a-z]+|[^a-z-]+$/g, ''))
        .filter(Boolean);
    let index = 0;
    while (index < words.length && LEADING_ADVERBS.has(words[index]!)) index += 1;
    const first = words[index] || '';
    if (!first) return false;
    if (STEP_VERBS.has(first)) return true;
    if (first.startsWith('re-') && STEP_VERBS.has(first.slice(3))) return true;
    return first.startsWith('re') && STEP_VERBS.has(first.slice(2));
}

/**
 * The strict text test for a subheading line written by Stage 3: ALL CAPS,
 * opens with a letter, at most SUBHEADING_MAX_WORDS words, no colon, no closing
 * full stop, no standard number, not a NOTE or images line, and its first word
 * is not an imperative step verb ("REMOVE ALL TILES" stays a work step).
 */
export function looksLikeSubheadingLine(value: unknown): boolean {
    const text = clean(value);
    if (!text) return false;
    if (/[a-z]/.test(text)) return false;
    if (!/^[A-Z]/.test(text)) return false;
    if ((text.match(/[A-Z]/g) || []).length < 2) return false;
    if (text.includes(':') || text.includes(';')) return false;
    if (/[.!?]$/.test(text)) return false;
    if (STANDARD_NUMBER_RE.test(text) || NOTE_LINE_RE.test(text) || IMAGES_LINE_RE.test(text)) return false;
    const words = text.split(' ').filter(word => /[A-Z0-9]/.test(word));
    if (words.length === 0 || words.length > SUBHEADING_MAX_WORDS) return false;
    return !opensWithStepVerb(text);
}

/** The `kind` a Stage 3 sequence step declares, lower-cased, or '' when it has none. */
export function sequenceStepKind(step: unknown): string {
    const kind = record(step)?.kind;
    return typeof kind === 'string' ? kind.trim().toLowerCase() : '';
}

// The identity used to find a line's source step: the same words, ignoring
// spacing, dash style, letter case and a closing full stop. Exact text, never
// similarity.
function lineKey(value: unknown): string {
    return clean(value)
        .replace(/[‐-―]/g, '-')
        .replace(/[.;:]+$/, '')
        .toLowerCase();
}

function kindsByLine(sourceSteps: unknown[] | undefined): Map<string, Set<string>> {
    const kinds = new Map<string, Set<string>>();
    for (const step of Array.isArray(sourceSteps) ? sourceSteps : []) {
        const kind = sequenceStepKind(step);
        if (!kind) continue;
        const key = lineKey(stage3SequenceStepLabel(step));
        if (!key) continue;
        const set = kinds.get(key) || new Set<string>();
        set.add(kind);
        kinds.set(key, set);
    }
    return kinds;
}

function canBeHeadingText(text: string): boolean {
    return Boolean(text) && !NOTE_LINE_RE.test(text) && !IMAGES_LINE_RE.test(text);
}

/**
 * Indexes of the string lines in `inclusions` that are subheadings. Pure: it
 * reads only its arguments and never looks at who edited the row.
 */
export function findSubheadingLineIndexes(inclusions: unknown, options: SubheadingSourceOptions = {}): number[] {
    if (!Array.isArray(inclusions)) return [];
    const kinds = kindsByLine(options.sourceSteps);
    const byLineage: number[] = [];
    const byText: number[] = [];
    inclusions.forEach((value, index) => {
        if (typeof value !== 'string') return;
        const text = clean(value);
        if (!text) return;
        const known = kinds.get(lineKey(text));
        if (known && known.size === 1) {
            if (known.has(SUBHEADING_STEP_KIND) && canBeHeadingText(text)) byLineage.push(index);
            return;
        }
        if (looksLikeSubheadingLine(text)) byText.push(index);
    });
    // Subheadings the item already has (heading objects, kind-marked lines)
    // show it is written with subheadings, so they count toward the threshold.
    // A payer fold merges items that each carried one subheading line. Each
    // distinct text counts once: a merged list can hold the same line twice
    // before its repeats are removed, and one line said twice is still one.
    const distinctHeadingTexts = new Set([
        ...[...byLineage, ...byText].map(index => lineKey(inclusions[index])),
        ...inclusions.filter(isHeadingInclusion).map(heading => lineKey(heading.text)),
    ].filter(Boolean));
    const indexes = distinctHeadingTexts.size >= SUBHEADING_MIN_TEXT_LINES
        ? [...byLineage, ...byText]
        : byLineage;
    return indexes.sort((left, right) => left - right);
}

/**
 * Turn the subheading lines of one inclusion list into heading objects. Returns
 * the same array when nothing changes.
 */
export function convertSubheadingLines<T>(inclusions: T[], options: SubheadingSourceOptions = {}): Array<T | HeadingInclusion> {
    if (!Array.isArray(inclusions)) return inclusions;
    const indexes = new Set(findSubheadingLineIndexes(inclusions, options));
    if (indexes.size === 0) return inclusions;
    return inclusions.map((value, index) => (
        indexes.has(index)
            ? { type: SUBHEADING_INCLUSION_TYPE, text: String(value).trim() }
            : value
    ));
}

/**
 * Remove lines from one inclusion list for a pass that drops repeated or
 * replaced lines (the physical bookends, the shared site logistics de-dupe).
 * `keepLine` is called once per line, in order, with the heading the line sits
 * under (null before the first heading), and never for a heading object, so a
 * heading is never removed as a protection or cleanup line. A heading whose
 * lines the pass removed, every one of them, is removed with them: it would
 * print as a section heading with nothing under it. A heading that had no
 * lines before the pass is kept as it was.
 */
export function filterInclusionLines<T>(
    inclusions: readonly T[],
    keepLine: (value: T, heading: HeadingInclusion | null) => boolean,
): T[] {
    const out: T[] = [];
    let openHeading = -1;
    let currentHeading: HeadingInclusion | null = null;
    let groupHadLines = false;
    let groupKeptLine = false;
    const closeGroup = () => {
        if (openHeading >= 0 && groupHadLines && !groupKeptLine) out.splice(openHeading, 1);
    };
    for (const value of inclusions) {
        if (isHeadingInclusion(value)) {
            closeGroup();
            openHeading = out.length;
            currentHeading = value;
            out.push(value);
            groupHadLines = false;
            groupKeptLine = false;
            continue;
        }
        groupHadLines = true;
        if (!keepLine(value, currentHeading)) continue;
        out.push(value);
        groupKeptLine = true;
    }
    closeGroup();
    return out;
}

/**
 * A line that belongs to the whole item, never to one subheading: a NOTE line,
 * an images line, and the site protection and cleanup lines the house bookends
 * stand for. The contract allows one of each per item, so a repeat of one under
 * another subheading is still a repeat.
 */
function isItemWideInclusionLine(value: unknown): boolean {
    const text = clean(typeof value === 'string' ? value : record(value)?.text);
    return Boolean(text) && (NOTE_LINE_RE.test(text) || IMAGES_LINE_RE.test(text) || isPhysicalScopeBookendLine(text));
}

interface SubheadingArea {
    units: Set<string>;
    words: Set<string>;
    /** The label's words in order, lower case. */
    label: string;
    /** The label is only a unit, lot or level locator ("UNIT 5", "LEVEL 1"). */
    locatorOnly: boolean;
}

// Words a label that only locates (a unit, a lot, a level, a block) is made
// of. Such a label names where an area is, not which area: "UNIT 5 - BALCONY"
// and "UNIT 5 - BATHROOM" are two areas.
const LOCATOR_LABEL_WORDS = new Set([
    'unit', 'units', 'lot', 'lots', 'level', 'levels', 'floor', 'floors', 'storey', 'storeys',
    'apartment', 'apartments', 'apt', 'apts', 'flat', 'flats', 'villa', 'villas', 'townhouse', 'townhouses',
    'block', 'blocks', 'building', 'buildings', 'tower', 'towers', 'no', 'number', 'and',
    'ground', 'basement', 'first', 'second', 'third', 'fourth', 'fifth', 'upper', 'lower', 'top',
]);

function isLocatorLabelWord(word: string): boolean {
    return LOCATOR_LABEL_WORDS.has(word) || /^[a-z]$/.test(word)
        || /^[a-z]?\d+[a-z]?$/.test(word) || /^\d+(?:st|nd|rd|th)$/.test(word);
}

// The area a subheading names, or null for a work-stream subheading. The house
// form is "<AREA> - <WORK>" ("ENTRY FOYER - SURFACE DRAINAGE"), so the area is
// the text before the dash. A subheading with no dash names an area only when
// it names a unit or lot ("UNIT 5 BALCONY"); "SUBSTRATE REPAIR" and "CLEAN UP"
// name none.
function subheadingArea(text: unknown): SubheadingArea | null {
    const heading = clean(text).replace(/[‐-―]/g, '-');
    if (!heading) return null;
    const dash = heading.search(/\s-\s/);
    const label = dash >= 0 ? heading.slice(0, dash) : heading;
    const units = new Set(extractUnitLocatorsFromText(label).map(locator => locator.key));
    if (dash < 0 && units.size === 0) return null;
    const ordered = label.toLowerCase().split(/[^a-z0-9]+/).filter(Boolean);
    const words = new Set(ordered);
    if (words.size === 0) return null;
    return {
        units,
        words,
        label: ordered.join(' '),
        locatorOnly: ordered.every(isLocatorLabelWord),
    };
}

function sameArea(left: SubheadingArea, right: SubheadingArea): boolean {
    if (left.units.size > 0 && right.units.size > 0
        && (left.units.size !== right.units.size || [...left.units].some(unit => !right.units.has(unit)))) {
        return false;
    }
    const within = (inner: Set<string>, outer: Set<string>) => [...inner].every(word => outer.has(word));
    return within(left.words, right.words) || within(right.words, left.words);
}

/**
 * Do two subheadings name different areas? True only when both name an area
 * (subheadingArea) and the areas differ: different units or lots, or area
 * words neither of which holds the other ("UNIT 5 BALCONY" and "UNIT 5
 * WINDOWS"; "UNIT 5" and "UNIT 5 BALCONY" are one area). The same line under
 * two such subheadings is each area's own work. A pair can be neither this nor
 * subheadingsNameSameArea ("UNIT 5" and "UNIT 5 BALCONY"); the repeat pass
 * then keeps both copies.
 */
export function subheadingsNameDifferentAreas(left: unknown, right: unknown): boolean {
    const leftArea = subheadingArea(left);
    const rightArea = subheadingArea(right);
    return Boolean(leftArea && rightArea && !sameArea(leftArea, rightArea));
}

/**
 * Do two subheadings name the same area? True only when both name an area
 * (subheadingArea) and the labels before the dash are the same words
 * ("UNIT 5 BALCONY - SUBSTRATE REPAIR" and "UNIT 5 BALCONY - WATERPROOFING").
 * One label inside another is not the same area ("BALCONY" and "UNIT 6
 * BALCONY"), and a label that is only a unit or level ("UNIT 5 - BALCONY" and
 * "UNIT 5 - BATHROOM", "LEVEL 1 - CORRIDOR" and "LEVEL 1 - STAIRWELL") never
 * is. A subheading whose area cannot be read ("ENSUITE", "BEDROOM 2",
 * "SUBSTRATE REPAIR") is never the same area as another, so its lines are
 * never taken for another subheading's.
 */
export function subheadingsNameSameArea(left: unknown, right: unknown): boolean {
    const leftArea = subheadingArea(left);
    const rightArea = subheadingArea(right);
    return Boolean(leftArea && rightArea
        && !leftArea.locatorOnly && !rightArea.locatorOnly
        && leftArea.label === rightArea.label);
}

/**
 * Remove repeated lines from one inclusion list. A work line repeats an
 * earlier copy under a heading of the same text, or under a subheading of the
 * same area (subheadingsNameSameArea): the barricade step under three UNIT 5
 * BALCONY subheadings is one step. Under any other heading the same words are
 * kept, because they can be another area's work: "Flood test the balcony for
 * 24 hours" under UNIT 5 BALCONY and again under UNIT 6 BALCONY is two flood
 * tests, and the same tile removal under MAIN BATHROOM and ENSUITE is two
 * bathrooms. An area QuoteMaker cannot read from the words is still an area,
 * so a repeat it cannot prove is kept; removing it would take that area off
 * the quote with its price still in it. A line above the first heading
 * repeats only an earlier line above the first heading (a merge puts one
 * row's flat lines there). A NOTE, images, site protection or cleanup line
 * belongs to the whole item (isItemWideInclusionLine), so it repeats an
 * earlier line anywhere in the list. A line under a priced heading (an
 * allowance) is compared only with lines under a heading of the same text, so
 * the pass never empties a priced heading. A list with no heading objects is
 * de-duplicated across the whole list, as before. A heading is never removed
 * for its own text, only with its lines when every one was a repeat
 * (filterInclusionLines), or when it repeats the heading directly above it
 * with nothing between. Exact text, never similarity; a line said once is
 * never removed; nothing is reordered or reworded. `lineKey` gives a line's
 * identity ('' keeps the line without checking it).
 */
export function dedupeInclusionLines<T>(
    inclusions: readonly T[],
    lineKey: (value: T) => string = value => (typeof value === 'string' ? clean(value) : ''),
): T[] {
    if (!Array.isArray(inclusions)) return inclusions as T[];
    const seenOnce = (seen: Set<string>, key: string) => {
        if (seen.has(key)) return false;
        seen.add(key);
        return true;
    };
    const itemWideSeen = new Set<string>();
    const pricedSeenByGroup = new Map<string, Set<string>>();
    // Each line's earlier copies: the heading text each sits under, null above
    // the first heading.
    const copiesByKey = new Map<string, Array<string | null>>();
    const kept = filterInclusionLines(inclusions, (value, heading) => {
        const key = lineKey(value);
        if (!key) return true;
        if (isItemWideInclusionLine(value)) return seenOnce(itemWideSeen, key);
        if (heading && hasOperatorInclusionPricingControl(heading)) {
            const group = `#${clean(heading.text).toLowerCase()}`;
            const seen = pricedSeenByGroup.get(group) || new Set<string>();
            pricedSeenByGroup.set(group, seen);
            return seenOnce(seen, key);
        }
        const copies = copiesByKey.get(key) || [];
        const headingText = heading ? clean(heading.text) : '';
        const repeat = heading
            ? copies.some(earlier => earlier !== null && (
                earlier.toLowerCase() === headingText.toLowerCase()
                || subheadingsNameSameArea(earlier, headingText)))
            : copies.length > 0;
        if (repeat) return false;
        copies.push(heading ? clean(heading.text) : null);
        copiesByKey.set(key, copies);
        return true;
    });
    return kept.filter((value, index) => !(
        index > 0
        && isHeadingInclusion(value)
        && isHeadingInclusion(kept[index - 1])
        && JSON.stringify(value) === JSON.stringify(kept[index - 1])
    ));
}

const BUILDER_EDITORS = new Set(['user', 'user-edit', 'manual', 'operator', 'assistant', 'assistant-edit']);

/**
 * Has a person (or the Assistant acting for one) edited this row's copy? Any
 * edit signal counts, so automation never restructures a row the builder
 * touched. The screens use it to offer "Make these subheadings" instead.
 */
export function isBuilderEditedScopeCopy(item: unknown): boolean {
    const row = record(item);
    if (!row) return false;
    const editor = clean(String(row.editedBy || row.edited_by || row.scopeEditedBy || row.scope_edited_by || ''))
        .toLowerCase();
    return row.manuallyEdited === true
        || row.manually_edited === true
        || row.manualEdited === true
        || row.userEdited === true
        || row.inclusionsEditedByUser === true
        || row.automationLocked === true
        || row._scopeAuthoritySource === 'operator-authored'
        || BUILDER_EDITORS.has(editor)
        || Boolean(
            row.humanEditedAt
            || row.human_edited_at
            || row.inclusionsEditedAt
            || row.inclusions_edited_at
            || row.scopeEditedAt
            || row.scope_edited_at
            || row.copyEditedAt
            || row.copy_edited_at,
        );
}

/** The Stage 3 sequence steps of the given activities, in order. */
export function subheadingSourceSteps(activities: unknown): unknown[] {
    return (Array.isArray(activities) ? activities : [])
        .flatMap(activity => {
            const steps = record(activity)?.sequence_of_works;
            return Array.isArray(steps) ? steps : [];
        });
}

/**
 * Convert one generated scope row's subheading lines. A builder-edited row is
 * returned unchanged. Returns the same object when nothing changes.
 */
export function applySubheadingsToScopeItem<T>(item: T, options: SubheadingSourceOptions = {}): T {
    const row = record(item);
    if (!row || !Array.isArray(row.inclusions) || isBuilderEditedScopeCopy(row)) return item;
    const inclusions = convertSubheadingLines(row.inclusions as unknown[], options);
    return inclusions === row.inclusions ? item : ({ ...row, inclusions } as T);
}

/** Convert every generated row in a scope; `sourceStepsFor` gives each row's source steps. */
export function applySubheadingsToScopeItems<T>(
    items: T[],
    sourceStepsFor: (item: T) => unknown[] = () => [],
): T[] {
    if (!Array.isArray(items)) return items;
    let changed = false;
    const next = items.map(item => {
        const converted = applySubheadingsToScopeItem(item, { sourceSteps: sourceStepsFor(item) });
        if (converted !== item) changed = true;
        return converted;
    });
    return changed ? next : items;
}
